import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { KPI_TOOLTIPS } from "../lib/tooltips";
import useCustomerData from "../hooks/useCustomerData";
import ConnectTallyBanner from "../components/ConnectTallyBanner";
import ProfileCompletionBanner from "../components/ProfileCompletionBanner";
import Spinner from "../components/Spinner";
import ErrorMessage from "../components/ErrorMessage";
import {
  ArrowDownRight,
  ArrowUpRight,
  Send,
  Sparkles,
  Clock,
  AlertTriangle,
  ShieldAlert,
  TrendingUp,
  Target,
  Wallet,
  Hourglass,
  TrendingUp as TrendIcon,
  CircleDollarSign,
  FileText,
  Users,
  RefreshCcw,
  Heart,
  Quote,
  Activity,
  Sunrise,
  Sun,
  Sunset,
  Moon,
} from "lucide-react";
import { Link } from "react-router-dom";
import { freshnessLabel, freshnessBadgeClasses } from "../lib/metricEnvelope";

/* -------------------------------------------------------------------------
   v2.0 — Boardroom layout (matches reference UI from 2026-06-04)
   • Personalised greeting strip ("Good morning, {name}")
   • Executive Briefing card with Risk / Opportunity / Action sub-cards
   • 8-tile KPI grid (cash, runway, revenue, profit, GST, debtors, cycle, health)
   • Revenue chart + Department Health (kept as the radar surrogate)
   • Drucker quote footer
   Real-data first; demo numbers gracefully appear only in `false` mode.
   ------------------------------------------------------------------------- */
const KPI_ICON_BY_ID = {
  cash_available:      Wallet,
  cash_runway:         Hourglass,
  revenue_mtd:         TrendIcon,
  net_profit_mtd:      CircleDollarSign,
  ebitda:              Activity,
  gst_due:             FileText,
  debtors_outstanding: Users,
  cash_cycle:          RefreshCcw,
  health_score:        Heart,
};
const PRIMARY_KPI_ORDER = [
  "cash_available", "cash_runway", "revenue_mtd", "net_profit_mtd",
  "ebitda", "gst_due", "debtors_outstanding", "health_score",
];

// Time-of-day bands per product spec:
//   05:00–11:59 -> Good morning   12:00–16:59 -> Good afternoon
//   17:00–20:59 -> Good evening   21:00–04:59  -> Good night
function greetingPeriod() {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return "morning";
  if (h >= 12 && h < 17) return "afternoon";
  if (h >= 17 && h < 21) return "evening";
  return "night";
}

function greetingFor() {
  const period = greetingPeriod();
  const LABELS = {
    morning: "Good morning",
    afternoon: "Good afternoon",
    evening: "Good evening",
    night: "Good night",
  };
  return LABELS[period];
}

// Apple-inspired greeting glyph: soft circular glassmorphic container,
// desaturated gradient tint per time-of-day, matching the boardroom's
// premium aesthetic (replaces the 👋 emoji).
const GREETING_ICON_STYLE = {
  morning:   { Icon: Sunrise, wrap: "from-amber-50 via-white to-orange-50/70 text-amber-500/90" },
  afternoon: { Icon: Sun,     wrap: "from-sky-50 via-white to-blue-50/70 text-sky-500/90" },
  evening:   { Icon: Sunset,  wrap: "from-orange-50 via-white to-rose-50/60 text-orange-500/90" },
  night:     { Icon: Moon,    wrap: "from-indigo-50 via-white to-ink-100/70 text-indigo-400/90" },
};

function GreetingGlyph() {
  const period = greetingPeriod();
  const { Icon, wrap } = GREETING_ICON_STYLE[period];
  return (
    <span
      data-testid="boardroom-greeting-icon"
      className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br ${wrap} border border-white/60 shadow-hover backdrop-blur-sm`}
    >
      <Icon size={19} strokeWidth={1.6} />
    </span>
  );
}

function firstNameOf(s) {
  return (s || "").trim().split(/\s+/)[0] || "";
}

const STATUS_STYLES = {
  green: { card: "bg-status-onTrack-bg border-status-onTrack-border", text: "text-status-onTrack-text", dot: "bg-status-onTrack" },
  amber: { card: "bg-status-drifting-bg border-status-drifting-border", text: "text-status-drifting-text", dot: "bg-status-drifting" },
  red:   { card: "bg-status-critical-bg border-status-critical-border", text: "text-status-critical-text", dot: "bg-status-critical" },
  blue:  { card: "bg-blue-100 border-blue-100", text: "text-blue-800", dot: "bg-blue-600" },
};

function statusFromScore(score) {
  if (score >= 75) return "green";
  if (score >= 60) return "amber";
  return "red";
}

function KpiGrid({ kpis }) {
  // Order to match the boardroom reference: cash → runway → revenue → profit,
  // then GST → debtors → cycle → health. Anything else (working capital,
  // gross margin, inventory…) goes behind the expand toggle.
  const byId = {};
  kpis.forEach((k) => { byId[k.id] = k; });
  const primary = PRIMARY_KPI_ORDER.map((id) => byId[id]).filter(Boolean);
  const more    = kpis.filter((k) => !PRIMARY_KPI_ORDER.includes(k.id));
  const [open, setOpen] = useState(false);

  return (
    <div data-testid="kpi-grid">
      <div className="grid grid-cols-2 gap-3 desktop:grid-cols-4">
        {primary.map((k) => <KPICard key={k.id} kpi={k} />)}
      </div>
      {more.length > 0 && (
        <>
          {open && (
            <div data-testid="kpi-grid-more" className="mt-3 grid grid-cols-2 gap-3 desktop:grid-cols-4">
              {more.map((k) => <KPICard key={k.id} kpi={k} />)}
            </div>
          )}
          <div className="mt-3 flex justify-center">
            <button
              data-testid="kpi-toggle-more"
              onClick={() => setOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-full border border-ink-200 bg-white px-3 py-1 text-[11.5px] font-medium text-ink-600 hover:border-cyan-300 hover:text-cyan-700 transition-colors"
            >
              {open ? "Hide" : "Show"} {more.length} more KPIs
              <span className={`text-[10px] transition-transform ${open ? "rotate-180" : ""}`}>▾</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------
   Executive Briefing card — derived from KPI snapshot.
   Pulls 3 things:
     • Headline health phrase ("Company Health is Stable / Strong / At Risk")
     • Revenue movement (revenue_mtd delta) → primary opportunity
     • Highest-severity dept-health red row → primary risk
   When real data is missing we soften copy to invite the user to connect.
   ------------------------------------------------------------------------- */
function ExecutiveBriefing({ company, kpis, deptHealth, dataAvailable }) {
  // Real customers may legitimately have `health_score === null` when the
  // calc engine doesn't have enough inputs (e.g. no revenue trend yet).
  // Visitors (demo mode) keep the seeded score so the landing dashboard
  // still looks alive.
  const rawScore = company?.health_score;
  const haveScore = rawScore !== null && rawScore !== undefined && rawScore !== "";
  const score = haveScore ? Number(rawScore) : null;
  const insufficient = true && !dataAvailable;

  let phrase, phraseColor;
  if (insufficient || !haveScore) {
    phrase = "Your next decision starts here.";
    phraseColor = "text-ink-400";
  } else if (score >= 75) { phrase = "Strong";          phraseColor = "text-status-onTrack-text"; }
  else if (score >= 60)    { phrase = "Stable";          phraseColor = "text-status-onTrack-text"; }
  else if (score >= 40)    { phrase = "Needs Attention"; phraseColor = "text-status-drifting-text";   }
  else                     { phrase = "At Risk";         phraseColor = "text-status-critical-text";     }

  const revKpi = kpis.find((k) => k.id === "revenue_mtd");
  const debtKpi = kpis.find((k) => k.id === "debtors_outstanding");
  const cashKpi = kpis.find((k) => k.id === "cash_runway");

  const ribbon = insufficient
    ? "Insufficient data to assess — connect Tally / Zoho to see your live executive briefing."
    : (!dataAvailable && !`Revenue ${revKpi?.delta_direction === "down" ? "is down" : "up"} ${revKpi?.delta || "—"} this month. Cash runway at ${cashKpi?.value || "—"}.`);

  const worstDept = (deptHealth || []).find((d) => d.status === "red") ||
                    (deptHealth || []).find((d) => d.status === "amber");

  // Honesty rule: when we don't have enough real data, show "Insufficient
  // data — sync your connectors to see this analysis" instead of any
  // fabricated verdict ("Receivables ₹X", "Stay the course", etc.).
  const INSUFFICIENT_CARD = {
    label: "Insufficient data",
    sub:   "Sync your connectors to see this analysis.",
    tone:  "neutral",
  };

  const risk = insufficient
    ? INSUFFICIENT_CARD
    : (worstDept
        ? { label: worstDept.name, sub: `${worstDept.metric_name || "metric"} at ${worstDept.metric_value ?? "—"}`, tone: worstDept.status === "red" ? "red" : "amber" }
        : (debtKpi && !debtKpi.unavailable
            ? { label: `Receivables ${debtKpi.value}`, sub: debtKpi.subtitle || "Track ageing", tone: "amber" }
            : { label: "—", sub: "Connect Tally to see risks", tone: "neutral" }));
  const opportunity = insufficient
    ? INSUFFICIENT_CARD
    : (revKpi && !revKpi.unavailable
        ? { label: `Revenue ${revKpi.delta || ""}`, sub: revKpi.subtitle || "vs last month", tone: "green" }
        : { label: "—", sub: "Pulls from Tally once connected", tone: "neutral" });
  const action = insufficient
    ? { label: "Sync your connectors", sub: "Tally / Zoho — unlock the full analysis", tone: "blue" }
    : (!dataAvailable && true
        ? { label: "Connect Tally", sub: "Unlock all 8 KPIs + AI insights", tone: "blue" }
        : (worstDept?.status === "red"
            ? { label: "Review red departments", sub: `Start with ${worstDept.name}`, tone: "blue" }
            // Honest "no urgent action" line — only when we actually have
            // enough data to make that call (no red depts AND we have a score).
            : (haveScore
                ? { label: "Stay the course", sub: "Health stable — no urgent action", tone: "blue" }
                : INSUFFICIENT_CARD)));

  const TONE = {
    red:     { bg: "bg-status-critical-bg", ring: "ring-status-critical-border", icon: "text-status-critical-text", Icon: ShieldAlert },
    amber:   { bg: "bg-status-drifting-bg", ring: "ring-status-drifting-border", icon: "text-status-drifting-text", Icon: ShieldAlert },
    green:   { bg: "bg-status-onTrack-bg", ring: "ring-status-onTrack-border", icon: "text-status-onTrack-text", Icon: TrendingUp },
    blue:    { bg: "bg-blue-50",    ring: "ring-blue-100",    icon: "text-blue-800",    Icon: Target },
    neutral: { bg: "bg-ink-50",   ring: "ring-ink-100",   icon: "text-ink-400",   Icon: Target },
  };

  return (
    <div data-testid="executive-briefing"
         className="rounded-2xl border border-ink-100 bg-white p-5 shadow-card">
      <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2 desktop:grid-cols-4">
        {/* Headline column */}
        <div className="desktop:col-span-1">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-400">
            Executive Briefing
          </div>
          <h2 className="mt-2 text-[20px] font-display font-bold leading-snug text-ink-950">
            {insufficient || !haveScore
              ? <span className={phraseColor}>{phrase}</span>
              : <>Company Health is <span className={phraseColor}>{phrase}</span></>}
          </h2>
          <p className="mt-2 text-[12.5px] leading-snug text-ink-500">{ribbon}</p>
        </div>

        {/* 3 sub-cards */}
        {[{ k: "Primary Risk", v: risk },
          { k: "Primary Opportunity", v: opportunity },
          { k: "Recommended Action", v: action }].map(({ k, v }) => {
          const t = TONE[v.tone] || TONE.neutral;
          const I = t.Icon;
          return (
            <div key={k}
                 data-testid={`briefing-${k.toLowerCase().replace(/\s+/g, "-")}`}
                 className="flex items-start gap-3 rounded-xl border border-ink-100 bg-white px-4 py-3">
              <span className={`mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${t.bg} ring-4 ${t.ring}`}>
                <I size={16} className={t.icon} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-400">{k}</div>
                <div className="mt-0.5 truncate text-[14px] font-semibold text-ink-950">{v.label}</div>
                <div className="text-[11.5px] text-ink-500">{v.sub}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DruckerQuote() {
  return (
    <div className="mt-5 flex items-center gap-3 rounded-2xl border border-ink-100 bg-white px-4 py-3">
      <Quote size={16} className="shrink-0 text-cyan-600" />
      <div className="text-[12.5px] italic text-ink-600">
        "The best way to predict the future is to create it."
        <span className="ml-1 not-italic text-ink-400">— Peter Drucker</span>
      </div>
    </div>
  );
}

function MiniSpark({ status }) {
  // Tiny decorative spark — purely visual; matches reference's micro-trend lines.
  const stroke =
    status === "green" ? "#009459" :
    status === "amber" ? "#A17400" :
    status === "red"   ? "#D73938" :
    "#034FAF";
  return (
    <svg viewBox="0 0 80 30" className="hidden tablet:block desktop:block h-7 w-20 shrink-0" preserveAspectRatio="none" aria-hidden="true">
      <polyline
        fill="none" stroke={stroke} strokeWidth="2"
        points="0,22 12,18 24,20 36,14 48,16 60,8 72,12 80,6"
      />
    </svg>
  );
}

function KPICard({ kpi }) {
  const s = STATUS_STYLES[kpi.status] || STATUS_STYLES.blue;
  const up = kpi.delta_direction === "up";
  const tip = KPI_TOOLTIPS[kpi.id];
  const unavailable = !!kpi.unavailable;
  const Icon = KPI_ICON_BY_ID[kpi.id] || TrendIcon;

  if (unavailable) {
    return (
      <div
        data-testid={`kpi-${kpi.id}`}
        className="group relative rounded-2xl border border-dashed border-ink-200 bg-white p-4 shadow-card transition-colors hover:border-cyan-300 hover:z-50 focus-within:z-50"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-ink-100">
              <Icon size={14} className="text-ink-400" />
            </span>
            <div className="text-[12.5px] font-medium text-ink-500">
              {kpi.label}
            </div>
          </div>
        </div>
        <div className="mt-2 text-[20px] tablet:text-[24px] desktop:text-[26px] font-bold leading-none text-ink-300">—</div>
        <div className="mt-2 text-[11.5px] text-ink-500">
          Connect <span className="font-semibold text-blue-800">{kpi.missing_reason}</span> to see this
        </div>
        {kpi.significance && (
          <div
            data-testid={`kpi-insight-${kpi.id}`}
            className="pointer-events-none absolute left-1/2 top-full z-[100] mt-2 w-72 -translate-x-1/2 rounded-lg bg-ink-950 px-3 py-2 text-[11.5px] leading-snug text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100"
          >
            <span className="absolute -top-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 bg-ink-950" />
            <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-cyan-300">
              <Sparkles size={10} /> Why this matters
            </div>
            {kpi.significance}
          </div>
        )}
      </div>
    );
  }

  // Icon-tint matches status colour.
  const iconBg =
    kpi.status === "green" ? "bg-status-onTrack-bg  text-status-onTrack-text" :
    kpi.status === "amber" ? "bg-status-drifting-bg text-status-drifting-text"   :
    kpi.status === "red"   ? "bg-status-critical-bg text-status-critical-text"     :
                             "bg-blue-50     text-blue-800";

  return (
    <div
      data-testid={`kpi-${kpi.id}`}
      className="group relative rounded-2xl border border-ink-100 bg-white p-4 shadow-card transition-transform hover:-translate-y-[1px] hover:shadow-hover hover:z-50 focus-within:z-50"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`inline-flex h-7 w-7 items-center justify-center rounded-lg ${iconBg}`}>
            <Icon size={14} />
          </span>
          <div className="text-[12.5px] font-medium text-ink-500">{kpi.label}</div>
        </div>
      </div>
      <div className="mt-2 flex items-end justify-between gap-2">
        <div className="text-[20px] tablet:text-[24px] desktop:text-[26px] font-display font-bold leading-none tabular-nums text-ink-950">{kpi.value}</div>
        <MiniSpark status={kpi.status} />
      </div>
      {kpi.data_envelope && (
        <div
          data-testid={`kpi-envelope-${kpi.id}`}
          className={`mt-2 inline-flex max-w-full items-center rounded-full border px-2 py-0.5 text-[10.5px] truncate ${freshnessBadgeClasses(kpi.data_envelope)}`}
          title={freshnessLabel(kpi.data_envelope)}
        >
          {freshnessLabel(kpi.data_envelope)}
        </div>
      )}
      <div className="mt-2 flex items-center gap-1 text-[11.5px]">
        {kpi.delta ? (
          <span className={`inline-flex items-center gap-0.5 font-semibold ${s.text}`}>
            {up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
            {kpi.delta}
          </span>
        ) : null}
        {!kpi.data_envelope && (
          <span className="text-ink-500">{kpi.delta && kpi.subtitle ? "· " : ""}{kpi.subtitle}</span>
        )}
      </div>
      {tip && (
        <div
          data-testid={`kpi-tip-${kpi.id}`}
          role="tooltip"
          className="pointer-events-none absolute left-1/2 top-full z-[100] mt-2 w-64 -translate-x-1/2 rounded-lg bg-ink-950 px-3 py-2 text-[11.5px] leading-snug text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100"
        >
          <span className="absolute -top-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 bg-ink-950" />
          {tip}
        </div>
      )}
    </div>
  );
}

function RevenueExpensesChart({ data }) {
  const max = Math.max(1, ...data.flatMap((d) => [d.revenue, d.expenses]));
  return (
    <div data-testid="revenue-expenses-card" className="rounded-xl border border-ink-200 bg-white p-5 shadow-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-500">
            Revenue vs Expenses
          </div>
          <div className="text-[13px] text-ink-500">Last 8 months · ₹ lakhs</div>
        </div>
        <div className="flex items-center gap-3 text-[11px]">
          <span className="inline-flex items-center gap-1.5 text-ink-600">
            <span className="h-2 w-2 rounded-sm bg-blue-800" /> Revenue
          </span>
          <span className="inline-flex items-center gap-1.5 text-ink-600">
            <span className="h-2 w-2 rounded-sm bg-blue-300" /> Expenses
          </span>
        </div>
      </div>

      <div className="mt-5 flex h-[160px] mobile:h-[140px] tablet:h-[180px] desktop:h-[200px] gap-1.5 desktop:gap-2 core-scroll-x">
        {data.map((d, i) => (
          <div
            key={`${d.month}-${i}`}
            data-testid={`chart-bar-${d.month.replace(/\s/g, "-")}-${i}`}
            className="flex h-full flex-1 flex-col"
          >
            <div className="flex flex-1 items-end gap-[3px]">
              <div
                className="flex-1 rounded-t-sm bg-blue-800 transition-all hover:opacity-90"
                style={{ height: `${(d.revenue / max) * 100}%` }}
                title={`Revenue ₹${d.revenue}L`}
              />
              <div
                className="flex-1 rounded-t-sm bg-blue-300 transition-all hover:opacity-90"
                style={{ height: `${(d.expenses / max) * 100}%` }}
                title={`Expenses ₹${d.expenses}L`}
              />
            </div>
            <div className="mt-1 text-center text-[10px] text-ink-500">{d.month}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PriorityActions({ items }) {
  return (
    <div data-testid="priority-actions-card" className="rounded-xl border border-ink-200 bg-white p-5 shadow-card">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-500">
            AI Priority Actions
          </div>
          <div className="text-[13px] text-ink-500">{items.length} items need your attention</div>
        </div>
        <Sparkles size={16} className="text-cyan-700" />
      </div>

      <ul className="mt-4 divide-y divide-ink-100">
        {items.map((a) => {
          const s = STATUS_STYLES[a.severity] || STATUS_STYLES.amber;
          return (
            <li
              key={a.id}
              data-testid={`priority-action-${a.id}`}
              className="flex items-start gap-3 py-3 first:pt-0 last:pb-0"
            >
              <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${s.dot}`} />
              <div className="flex-1">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-[13px] font-semibold text-ink-950">{a.title}</div>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-medium ${s.card} ${s.text}`}>
                    <Clock size={10} className="-mt-px mr-0.5 inline" />
                    {a.due}
                  </span>
                </div>
                <div className="mt-0.5 text-[12px] text-ink-600">{a.detail}</div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function DepartmentHealth({ items }) {
  return (
    <div data-testid="department-health-card" className="rounded-xl border border-ink-200 bg-white p-5 shadow-card">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-500">
        Department Health
      </div>
      <div className="text-[13px] text-ink-500">Live scores from connected systems</div>

      <ul className="mt-4 space-y-3">
        {items.map((d) => {
          const status = d.status || statusFromScore(d.score);
          const s = STATUS_STYLES[status] || STATUS_STYLES.blue;
          return (
            <li key={d.name} data-testid={`dept-${d.name.toLowerCase().replace(/\s|&/g, "-")}`}>
              <div className="flex items-center justify-between text-[12.5px]">
                <span className="font-medium text-ink-950">{d.name}</span>
                <span className={`font-semibold ${s.text}`}>{d.score}</span>
              </div>
              <div className="mt-1 h-1.5 w-full rounded-full bg-ink-100">
                <div
                  className={`h-1.5 rounded-full ${
                    status === "green" ? "bg-status-onTrack"
                    : status === "amber" ? "bg-status-drifting"
                    : "bg-status-critical"
                  }`}
                  style={{ width: `${Math.max(0, Math.min(100, d.score))}%`, transition: "width 700ms ease" }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Chat-input chips. Generic, real-data-prompting suggestions — no demo company
// names or figures. The chat reply uses the customer's actual context fetched
// at runtime so the AI always answers from the user's real numbers.
const QUICK_PROMPTS = [
  "What's my cash position?",
  "GST status",
  "Which customers owe me the most?",
  "How many days of runway do I have?",
];

function TypingDots() {
  return (
    <div data-testid="chat-typing" className="max-w-[85%] rounded-2xl bg-blue-50 px-4 py-3">
      <div className="flex items-center gap-1">
        <span className="h-1.5 w-1.5 rounded-full bg-ink-400 core-pulse" style={{ animationDelay: "0ms" }} />
        <span className="h-1.5 w-1.5 rounded-full bg-ink-400 core-pulse" style={{ animationDelay: "200ms" }} />
        <span className="h-1.5 w-1.5 rounded-full bg-ink-400 core-pulse" style={{ animationDelay: "400ms" }} />
      </div>
    </div>
  );
}

function AIChatPanel() {
  // Personalised greeting: use the logged-in customer's first name when available.
  const greetingName = (() => {
    try {
      const raw = sessionStorage.getItem("current_customer");
      if (!raw) return "";
      const c = JSON.parse(raw);
      const first = (c?.name || "").trim().split(/\s+/)[0];
      return first || "";
    } catch (_e) {
      return "";
    }
  })();
  const [messages, setMessages] = useState([
    {
      role: "ai",
      text: `Hi ${greetingName} — I'm your CFO. Ask me anything about cash, margins, debtors, GST, or your team's performance.`,
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sessionIdRef = useRef(`core-cfo-${Math.random().toString(36).slice(2, 10)}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  // The TopBar Ask C-ORE button now handles scroll + focus via DOM polling so
  // we no longer need the window event listener / pending flag. Keeping
  // this file free of those plumbing concerns.

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || sending) return;
    setMessages((m) => [...m, { role: "user", text: t }]);
    setInput("");
    setSending(true);

    const res = await api.chat(t, sessionIdRef.current);
    const reply = res.ok
      ? (res.data?.reply || "Sorry, I could not process that right now. Please try again in a moment.")
      : (res.error || "Sorry, I could not process that right now. Please try again in a moment.");
    setMessages((m) => [...m, { role: "ai", text: reply }]);
    setSending(false);
  };

  return (
    <div data-testid="ai-chat-card" className="flex flex-col rounded-xl border border-ink-200 bg-white shadow-card">
      <div className="flex items-center justify-between border-b border-ink-100 px-5 py-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-500">
            Ask C-ORE
          </div>
          <div className="text-[13px] text-ink-500">Context-aware Q&amp;A · Hindi or English</div>
        </div>
        <Sparkles size={16} className="text-cyan-700" />
      </div>

      <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto px-5 py-3" style={{ maxHeight: 260 }}>
        {messages.map((m, i) => (
          <div
            key={i}
            data-testid={`chat-msg-${i}`}
            className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-[12.5px] leading-snug ${
              m.role === "ai"
                ? "bg-blue-50 text-ink-950"
                : "ml-auto bg-blue-800 text-white"
            }`}
          >
            {m.text}
          </div>
        ))}
        {sending && <TypingDots />}
      </div>

      <div className="border-t border-ink-100 px-5 py-3">
        <div className="mb-2 flex flex-wrap gap-1.5">
          {QUICK_PROMPTS.map((p) => (
            <button
              key={p}
              data-testid={`chat-prompt-${p.slice(0, 14).replace(/\s/g, "-")}`}
              onClick={() => send(p)}
              disabled={sending}
              className="rounded-full border border-ink-200 bg-white px-2.5 py-1 text-[11px] text-ink-600 hover:border-cyan-300 hover:text-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {p}
            </button>
          ))}
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="flex items-center gap-2"
        >
          <input
            data-testid="chat-input"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={sending ? "Thinking…" : "Ask anything…"}
            disabled={sending}
            className="flex-1 rounded-lg border border-ink-200 bg-white px-3 py-2 text-[12.5px] text-ink-950 placeholder:text-ink-400 focus:border-cyan-600 focus:outline-none disabled:bg-ink-50"
          />
          <button
            type="submit"
            data-testid="chat-send"
            disabled={sending || !input.trim()}
            className="inline-flex items-center gap-1 rounded-lg bg-cyan-700 px-3 py-2 text-[12.5px] font-semibold text-white hover:bg-cyan-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send size={13} /> Send
          </button>
        </form>
      </div>
    </div>
  );
}

function OfflineBanner({ supabaseStatus }) {
  if (!supabaseStatus || supabaseStatus.ok) return null;
  return (
    <div
      data-testid="offline-banner"
      className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-800"
    >
      <AlertTriangle size={14} />
      Working in offline mode. {supabaseStatus.message || "We can't reach Supabase right now."}
    </div>
  );
}

export default function ExecutiveDashboard({ supabaseStatus, onCompanyLoaded }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { dataAvailable, customerData, isLoading } = useCustomerData();

  const load = async () => {
    setLoading(true);
    setError(null);
    const res = await api.executiveSummary();
    if (!res.ok) {
      setError(res.error);
      setLoading(false);
      return;
    }
    setData(res.data);
    if (res.data?.company && onCompanyLoaded) onCompanyLoaded(res.data.company);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [dataAvailable]); // re-fetch once we know the customer's data state

  if (loading) {
    return (
      <div className="px-6 py-10">
        <Spinner label="Loading your dashboard…" testId="dashboard-spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-6 py-10">
        <ErrorMessage message={error} onRetry={load} testId="dashboard-error" />
      </div>
    );
  }

  // Active customer + no real data → force every KPI into the dashed
  // "unavailable" state regardless of what the backend returned. This is
  // the belt-and-braces guarantee that a logged-in user never sees
  // hardcoded demo numbers (₹14.2L, ₹48.3L, …).
  const forceUnavailable = true && !dataAvailable;
  const kpisGated = (data?.kpis || []).map((k) => (
    forceUnavailable
      ? { ...k, unavailable: true, value: "—", delta: "", subtitle: "Connect Tally", missing_reason: k.missing_reason || "Tally" }
      : k
  ));
  // Backend now returns customer-specific (often empty) arrays when
  // customer_id is set. We additionally force-empty for the no-data state to
  // be doubly safe in case of any backend mishap.
  const priorityActionsGated = forceUnavailable ? [] : (data?.priority_actions || []);
  const deptHealthGated      = forceUnavailable ? [] : (data?.department_health || []);
  const revExpGated          = forceUnavailable ? [] : (data?.revenue_expenses || []);
  // Logged-in customer + backend returned an empty array (no history yet) =>
  // render the empty-state card even when dataAvailable is true.
  const showEmptyChart   = forceUnavailable || (true && revExpGated.length === 0);
  const showEmptyActions = forceUnavailable || (true && priorityActionsGated.length === 0);
  const showEmptyDept    = forceUnavailable || (true && deptHealthGated.length === 0);

  // Personalised greeting (matches the reference's "Good morning, Aditya 👋")
  const greetingName = (() => {
    try {
      const raw = sessionStorage.getItem("current_customer");
      const c = raw ? JSON.parse(raw) : null;
      return firstNameOf(c?.name) || firstNameOf(data?.company?.owner_name) || "there";
    } catch {
      return firstNameOf(data?.company?.owner_name) || "there";
    }
  })();
  const todayStr = data?.company?.current_date || new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

  return (
    <div data-testid="executive-dashboard" className="bg-[var(--color-page)]">
      <ConnectTallyBanner dataAvailable={dataAvailable} isLoading={isLoading} />
      <ProfileCompletionBanner customerData={customerData} isLoading={isLoading} />
      <div className="px-4 tablet:px-5 desktop:px-6 py-4 desktop:py-5">
      <OfflineBanner supabaseStatus={supabaseStatus} />

      {/* Boardroom greeting strip */}
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <h1 data-testid="boardroom-greeting" className="font-display text-[20px] tablet:text-[24px] desktop:text-[26px] font-bold leading-tight text-ink-950 truncate">
              {greetingFor()}, {greetingName.toUpperCase()}
            </h1>
            <GreetingGlyph />
          </div>
          <p className="mt-1 text-[12.5px] text-ink-500">
            Here's your executive briefing for <span className="font-medium text-ink-700">{todayStr}</span>.
          </p>
        </div>
        <div className="hidden md:flex items-center gap-2 rounded-full border border-ink-200 bg-white px-3 py-1.5 text-[12px] text-ink-600 shadow-card">
          <Clock size={12} className="text-ink-400" />
          <span>{todayStr}</span>
        </div>
      </div>

      {/* Executive Briefing — Reference card with Risk / Opportunity / Action */}
      <ExecutiveBriefing
        company={data?.company}
        kpis={kpisGated}
        deptHealth={deptHealthGated}
        dataAvailable={dataAvailable}
      />

      {/* 8 boardroom KPI tiles */}
      <div className="mt-5">
        <KpiGrid kpis={kpisGated} />
      </div>

      {/* Middle Row: Revenue chart + AI Priority Actions */}
      <div className="mt-5 grid grid-cols-1 gap-4 tablet:grid-cols-2 desktop:grid-cols-5">
        <div className="desktop:col-span-3">
          {showEmptyChart
            ? <EmptyChartCard />
            : <RevenueExpensesChart data={revExpGated} />}
        </div>
        <div className="desktop:col-span-2">
          {showEmptyActions
            ? <EmptyActionsCard />
            : <PriorityActions items={priorityActionsGated} />}
        </div>
      </div>

      {/* Bottom Row: Department Health + Ask C-ORE */}
      <div className="mt-4 grid grid-cols-1 gap-4 tablet:grid-cols-2 desktop:grid-cols-5">
        <div className="desktop:col-span-2">
          {showEmptyDept
            ? <EmptyDeptCard />
            : <DepartmentHealth items={deptHealthGated} />}
        </div>
        <div className="desktop:col-span-3">
          <AIChatPanel />
        </div>
      </div>

      <DruckerQuote />
      </div>
    </div>
  );
}

function EmptyChartCard() {
  return (
    <div data-testid="revenue-expenses-card-empty" className="rounded-xl border border-dashed border-blue-200 bg-white p-5 shadow-card">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-500">Revenue vs Expenses</div>
      <div className="text-[13px] text-ink-500">Last 8 months · ₹ lakhs</div>
      <div className="mt-5 flex h-[200px] items-center justify-center rounded-md border border-dashed border-blue-200 bg-blue-50/50 text-center text-[12px] text-blue-700">
        Connect Tally to see your live revenue vs expenses chart across the last 8 months.
      </div>
    </div>
  );
}

function EmptyActionsCard() {
  return (
    <div data-testid="priority-actions-card-empty" className="rounded-xl border border-dashed border-blue-200 bg-white p-5 shadow-card">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-500">AI Priority Actions</div>
      <div className="text-[13px] text-ink-500">Personalised once your Tally is connected</div>
      <div className="mt-4 rounded-md border border-dashed border-blue-200 bg-blue-50/50 px-3 py-6 text-center text-[12px] text-blue-700">
        We'll surface the highest-impact actions to take this week, generated from your real numbers.
      </div>
    </div>
  );
}

function EmptyDeptCard() {
  return (
    <div data-testid="department-health-card-empty" className="rounded-xl border border-dashed border-blue-200 bg-white p-5 shadow-card">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-500">Department Health</div>
      <div className="text-[13px] text-ink-500">Live scores from connected systems</div>
      <div className="mt-4 rounded-md border border-dashed border-blue-200 bg-blue-50/50 px-3 py-6 text-center text-[12px] text-blue-700">
        Department health scores light up as soon as your first Tally sync lands.
      </div>
    </div>
  );
}
